import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Drawer,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  Paper,
  Button,
  Alert,
  Snackbar,
  Backdrop,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import { LoaderWrapper } from '../../components/index';

// Import API services
import { 
  getApplicationForm, 
  saveApplicationForm, 
  submitApplication,
  getLabById,
  getApplicationId 
} from '../../services/api';

// Import all calibration form sections
import NonAnalyticalInstrumentsSection from './applicationForm/NonAnalyticalInstrumentsSection';
import LaboratorySection from './applicationForm/LaboratorySection';
import LaboratorySectionAssessment from './applicationForm/LaboratorySectionAssessment';
import PersonnelSection from './applicationForm/PersonnelSection';
import EquipmentSection from './applicationForm/EquipmentSection';
import ScopeSection from './applicationForm/ScopeSection';
import ScopeSectionAssessment from './applicationForm/ScopeSectionAssessment';
import InfrastructureSection from './applicationForm/InfrastructureSection';
import DocumentationSection from './applicationForm/DocumentationSection';
import OrganizationSection from './applicationForm/OrganizationSection';
import OrganizationSectionAssessment from './applicationForm/OrganizationSectionAssessment';
import ProficiencyTestingSection from './applicationForm/ProficiencyTestingSection';
import DisciplineSection from './applicationForm/DisciplineSection';
import DisciplineSectionAssessment from './applicationForm/DisciplineSectionAssessment';
import LabStaffSection from './applicationForm/LabStaffSection';
import LabStaffSectionAdditional from './applicationForm/LabStaffSectionAdditional';
import LabStaffSectionAssessment from './applicationForm/LabStaffSectionAssessment';
import FeesSection from './applicationForm/FeesSection';
import ReferenceMaterial from './applicationForm/ReferenceMaterial';
import EnclosureSection from './applicationForm/EnclosureSection';
import DeclarationSection from './applicationForm/DeclarationSection';
import DeclarationSectionAdditional from './applicationForm/DeclarationSectionAdditional';
import PhantomSection from './applicationForm/PhantomSection';
import RadioisotopeSection from './applicationForm/RadioisotopeSection';
import FinalSubmitSection from './applicationForm/FinalSubmitSection';
import SubUserSection from './applicationForm/SubUserSection';
import FinalAssessorSubmitSection from './applicationForm/FinalAssessorSubmitSection';
import FinalSubmitSectionAdditionalApp from './applicationForm/FinalSubmitSectionAdditionalApp';

const DRAWER_WIDTH = 320;
const COLLAPSED_DRAWER_WIDTH = 70;
const APP_HEADER_HEIGHT = 64; // Height of the existing ApplicationHeader

const MedicalLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentSection, setCurrentSection] = useState('laboratory-details');
  const [formData, setFormData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [labData, setLabData] = useState(null);
  const [appId, setAppId] = useState(null);
  const [showDeclaration, setShowDeclaration] = useState(false);
  const [showEnclosuresComplete, setShowEnclosuresComplete] = useState(false);

  // Section configuration
  const sections = useMemo(() => [
    {
      id: 'laboratory-details',
      title: 'Laboratory Details',
      component: LaboratorySection,
      path: '/main/medical/laboratory-details',
    },
    {
      id: 'laboratory-detailsAssessment',
      title: 'Laboratory Details',
      component: LaboratorySectionAssessment,
      path: '/main/medical/laboratory-detailsAssessment',
    },
    {
      id: 'organization',
      title: 'Organization',
      component: OrganizationSection,
      path: '/main/medical/organization',
    },
    {
      id: 'organizationAssessment',
      title: 'Organization',
      component: OrganizationSectionAssessment,
      path: '/main/medical/organizationAssessment',
    },
    {
      id: 'proficiencyTesting',
      title: 'Proficiency Testing',
      component: ProficiencyTestingSection,
      path: '/main/medical/proficiency-testing',
    },
    {
      id: 'discipline',
      title: 'Discipline',
      component: DisciplineSection,
      path: '/main/medical/discipline',
    },
    {
      id: 'disciplineAssessment',
      title: 'Discipline',
      component: DisciplineSectionAssessment,
      path: '/main/medical/disciplineAssessment',
    },
    {
      id: 'labStaff',
      title: 'Lab Staff',
      component: LabStaffSection,
      path: '/main/medical/lab-staff',
    },
    {
      id: 'labStaff-additional',
      title: 'Lab Staff',
      component: LabStaffSectionAdditional,
      path: '/main/medical/lab-staff-additional',
    },
    {
      id: 'labStaffAssessment',
      title: 'Lab Staff',
      component: LabStaffSectionAssessment,
      path: '/main/medical/lab-staffAssessment',
    },
    {
      id: 'subUser',
      title: 'Create Sub User',
      component: SubUserSection,
      path: '/main/medical/sub-user',
    },
    {
      id: 'nonAnalyticalInstruments',
      title: 'Non Analytical Instruments',
      component: NonAnalyticalInstrumentsSection,
      path: '/main/medical/non-analytical-instruments',
    },
    {
      id: 'scope',
      title: 'Scope',
      component: ScopeSection,
      path: '/main/medical/scope',
    },
    {
      id: 'scopeAssessment',
      title: 'Scope',
      component: ScopeSectionAssessment,
      path: '/main/medical/scopeAssessment',
    },
    {
      id: 'fees',
      title: 'Fees',
      component: FeesSection,
      path: '/main/medical/fees',
    },
    {
      id: 'enclosure',
      title: 'Enclosure',
      component: EnclosureSection,
      path: '/main/medical/enclosure',
    },
    {
      id: 'declaration',
      title: 'Declaration',
      component: DeclarationSection,
      path: '/main/medical/declaration',
    },
    {
      id: 'declaration-additional',
      title: 'Declaration',
      component: DeclarationSectionAdditional,
      path: '/main/medical/declaration-additional',
    },
    {
      id: 'equipment',
      title: 'Equipment',
      component: EquipmentSection,
      path: '/main/medical/equipment',
    },
    {
      id: 'scope',
      title: 'Calibration Scope',
      component: ScopeSection,
      path: '/main/medical/medical-scope',
    },
    {
      id: 'infrastructure',
      title: 'Infrastructure',
      component: InfrastructureSection,
      path: '/main/medical/measurement-uncertainty',
    },
    {
      id: 'documentation',
      title: 'Documentation',
      component: DocumentationSection,
      path: '/main/medical/management-system',
    },
    {
      id: 'listofphantoms',
      title: 'List of Phantoms',
      component: PhantomSection,
      path: '/main/medical/phantoms',
    },
    {
      id: 'radioisotopes',
      title: 'Radioisotopes/Test Kits',
      component: RadioisotopeSection,
      path: '/main/medical/radioisotopes',
    },
    {
      id: 'final-submit',
      title: 'Final Submit',
      component: FinalSubmitSection,
      path: '/main/medical/final-submit',
    },
    {
      id: 'final-assessor-submit',
      title: 'Final Assessor Submit',
      component: FinalAssessorSubmitSection,
      path: '/main/medical/final-assessor-submit',
    },
    {
      id: 'final-submit-additional',
      title: 'Final Submit',
      component: FinalSubmitSectionAdditionalApp,
      path: '/main/medical/final-submit-additional',
    },
  ], []);

  // Determine current section based on route
  useEffect(() => {
    const currentPath = location.pathname;
    const section = sections.find(s => s.path === currentPath);
    if (section) {
      setCurrentSection(section.id);
    }
  }, [location.pathname, sections]);

  // Check enclosure completion status to show/hide declaration
  useEffect(() => {
    const checkEnclosureCompletion = () => {
      // Check if enclosure section is complete based on localStorage flag
      const isEnclosureCompleted = localStorage.getItem('medicalEnclosureCompleted') === 'true';
      setShowDeclaration(isEnclosureCompleted);
      setShowEnclosuresComplete(isEnclosureCompleted); // Also set for Final Submit condition
    };

    checkEnclosureCompletion();
    
    // Listen for storage changes to update when enclosure status changes
    const handleStorageChange = (e) => {
      if (e.key === 'medicalEnclosureCompleted') {
        const isCompleted = e.newValue === 'true';
        setShowDeclaration(isCompleted);
        setShowEnclosuresComplete(isCompleted); // Also update Final Submit condition
      }
    };

    // Listen for custom event from EnclosureSection
    const handleEnclosureStatusChange = (e) => {
      setShowDeclaration(e.detail.completed);
      setShowEnclosuresComplete(e.detail.completed); // Also update Final Submit condition
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('enclosureStatusChanged', handleEnclosureStatusChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('enclosureStatusChanged', handleEnclosureStatusChange);
    };
  }, [formData, completedSections]);

  // Load initial data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get user info from localStorage or token
        const orgId = localStorage.getItem('orgId');

        if (!orgId) {
          setError('No organization ID found. Please login again.');
          navigate('/main/login');
          return;
        }

      } catch (error) {
        console.error('Error loading initial data:', error);
        setError('Failed to load application data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Load saved data and application data
  useEffect(() => {
    const savedData = localStorage.getItem('medicalFormData');
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }

    const savedCompletedSections = localStorage.getItem('medicalCompletedSections');
    if (savedCompletedSections) {
      setCompletedSections(JSON.parse(savedCompletedSections));
    }
  }, []);

  // Load form data from API
  const loadFormData = async (labTypeId, orgId) => {
    try {
      const response = await getApplicationForm(labTypeId, orgId);
      if (response.data && response.data.success) {
        setFormData(response.data.data || {});
      }
    } catch (error) {
      console.error('Error loading form data:', error);
      // Continue with empty form if loading fails
    }
  };

  // Save form data to API and localStorage
  const saveFormData = async (sectionId = null, data = null) => {
    try {
      setSaveLoading(true);
      
      const dataToSave = sectionId && data 
        ? { ...formData, [sectionId]: { ...formData[sectionId], ...data } }
        : formData;

      // Save to localStorage
      localStorage.setItem('medicalFormData', JSON.stringify(dataToSave));

      // Save to API if we have appId
      if (appId) {
        const payload = {
          appId: appId,
          sectionId: sectionId || currentSection,
          formData: dataToSave,
          labTypeId: 3, // Medical lab type
        };

        await saveApplicationForm(payload);
        setSnackbarOpen(true);
      }

    } catch (error) {
      console.error('Error saving form data:', error);
      setError('Failed to save form data. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFormDataUpdate = async (sectionId, data) => {
    const updatedFormData = {
      ...formData,
      [sectionId]: {
        ...formData[sectionId],
        ...data,
      },
    };
    
    setFormData(updatedFormData);
    
    // Auto-save to API and localStorage
    await saveFormData(sectionId, data);
  };




  const handleSectionComplete = async (sectionId) => {
    if (!completedSections.includes(sectionId)) {
      const updatedCompleted = [...completedSections, sectionId];
      setCompletedSections(updatedCompleted);
      localStorage.setItem('medicalCompletedSections', JSON.stringify(updatedCompleted));
      
      // Save completion status to API
      await saveFormData(sectionId, { completed: true });
    }
  };

  const navigateToSection = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (section) {
      navigate(section.path);
    }
  };

  const getCurrentSectionIndex = () => {
    return sections.findIndex(s => s.id === currentSection);
  };

  const handlePreviousSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex > 0) {
      navigateToSection(sections[currentIndex - 1].id);
    }
  };

  // Submit complete application
  const submitCompleteApplication = async () => {
    try {
      setLoading(true);
      
      if (!appId) {
        setError('No application ID found. Please refresh the page.');
        return;
      }

      const payload = {
        appId: appId,
        formData: formData,
        labTypeId: 3, // Medical lab type
        completedSections: completedSections,
      };

      await submitApplication(payload);
      
      // Show success message and redirect
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate('/main/dashboard'); // or wherever you want to redirect after submission
      }, 2000);

    } catch (error) {
      console.error('Error submitting application:', error);
      setError('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNextSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex < sections.length - 1) {
      navigateToSection(sections[currentIndex + 1].id);
    } else {
      // Last section - submit application
      submitCompleteApplication();
    }
  };

  const renderCurrentSection = () => {
    const section = sections.find(s => s.id === currentSection);
    if (!section) return null;

    const SectionComponent = section.component;
    
    // For EnclosureSection, pass all form data for validation
    if (currentSection === 'enclosure') {
      return (
        <SectionComponent
          data={formData[currentSection] || {}}
          allFormData={formData}
          onUpdate={(data) => handleFormDataUpdate(currentSection, data)}
          onComplete={() => handleSectionComplete(currentSection)}
        />
      );
    }
    
    return (
      <SectionComponent
        data={formData[currentSection] || {}}
        onUpdate={(data) => handleFormDataUpdate(currentSection, data)}
        onComplete={() => handleSectionComplete(currentSection)}
      />
    );
  };

  const currentSectionIndex = getCurrentSectionIndex();
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex === sections.length - 1;

  const drawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', height: '100vh', pt: `${APP_HEADER_HEIGHT}px` }}>
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid #e0e0e0',
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            top: `${APP_HEADER_HEIGHT}px`,
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        <Sidebar
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
          activeStep={currentSectionIndex}
          onSectionClick={(item) => navigate(item.path)}
          showDeclaration={showDeclaration}
          showEnclosuresComplete={showEnclosuresComplete}
          collapsed={sidebarCollapsed}
          onCollapseToggle={handleSidebarCollapse}
        />
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { lg: sidebarOpen ? 0 : 0 },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          position: 'relative',
        }}
      >
        {/* Toggle Sidebar Button for Desktop when Sidebar is Closed */}
        {isMobile && !sidebarOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: `${APP_HEADER_HEIGHT + 10}px`,
              left: 10,
              zIndex: theme.zIndex.drawer + 1,
            }}
          >
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Container maxWidth="xl" sx={{ py: 3, height: '100%' }}>
          {/* Loading Indicator */}
          {loading && (
            <LoaderWrapper 
              loading={true} 
              type="page" 
              message="Loading application data..." 
            />
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Save Status Indicator */}
          {saveLoading && (
            <LoaderWrapper 
              loading={true} 
              type="inline" 
              message="Saving form data..." 
              size="small"
            />
          )}

          {/* Form Content */}
          <Paper sx={{ minHeight: 'calc(100vh - 280px)', mb: 3 }}>
            {location.pathname === '/main/medical' ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ mb: 2, color: '#00024c' }}>
                  Welcome to Medical Laboratory Application
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Please select a section from the sidebar to begin filling out your application.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigateToSection('laboratory-details')}
                >
                  Start Application
                </Button>
              </Box>
            ) : location.pathname === '/main/medical/reference-material' ? (
              <ReferenceMaterial />
            ) : location.pathname === '/main/medical/final-submit' ? (
              <FinalSubmitSection />
            ) : location.pathname === '/main/medical/final-assessor-submit' ? (
              <FinalSubmitSection />
            ) : (
              renderCurrentSection()
            )}
          </Paper>

          {/* Navigation Controls */}
          {/* {location.pathname !== '/main/medical' && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handlePreviousSection}
                disabled={isFirstSection}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={handleNextSection}
                disabled={isLastSection}
              >
                {isLastSection ? 'Complete' : 'Next'}
              </Button>
            </Box>
          )} */}
        </Container>
      </Box>


      {/* Save Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Form data saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MedicalLayout;